/* ============================================================================
   MONARA.Mixer — el hero mezclador.

   Cada franja es un carrusel horizontal con scroll-snap. El módulo sigue qué
   pieza está activa en cada franja, recalcula el precio del conjunto y arma
   el carrito con las tres piezas de un solo movimiento.

   Decisiones que vale la pena tener presentes:
   - El estado vive en el scroll, no en una variable aparte. Así el gesto del
     dedo y los botones de flecha no se pueden desincronizar.
   - Si una pieza tiene una sola variante se agrega directo; si tiene varias,
     se abre la hoja de tallas. No se asume talla por el cliente.
   - Si una pieza está agotada, el botón lo dice en vez de fallar al agregar.
   ========================================================================== */
(function () {
  'use strict';

  window.MONARA = window.MONARA || {};

  MONARA.Mixer = {
    init: function () {
      var roots = document.querySelectorAll('[data-mm-mixer]');
      if (!roots.length) return;
      Array.prototype.forEach.call(roots, function (root) {
        new Mixer(root);
      });
    }
  };

  function Mixer(root) {
    this.root = root;
    this.bands = Array.prototype.slice.call(root.querySelectorAll('[data-mm-band]'));
    this.nameEl = root.querySelector('[data-mm-mixer-name]');
    this.priceEl = root.querySelector('[data-mm-mixer-price]');
    this.addBtn = root.querySelector('[data-mm-mixer-add]');
    this.seal = root.querySelector('[data-mm-mixer-seal]');
    this.sheet = document.querySelector('[data-mm-mixer-sheet]');

    this.namePrefix = this.nameEl ? this.nameEl.textContent.trim() : '';

    if (!this.bands.length) return;

    this.bindBands();
    this.bindAdd();
    this.bindSheet();
    this.update();
  }

  /* ---------- franjas ---------- */

  Mixer.prototype.bindBands = function () {
    var self = this;

    this.bands.forEach(function (band) {
      var track = band.querySelector('[data-mm-band-track]');
      var prev = band.querySelector('[data-mm-band-prev]');
      var next = band.querySelector('[data-mm-band-next]');
      if (!track) return;

      var raf = null;
      track.addEventListener('scroll', function () {
        if (raf) window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(function () { self.update(); });
      }, { passive: true });

      if (prev) prev.addEventListener('click', function () { self.step(band, -1); });
      if (next) next.addEventListener('click', function () { self.step(band, 1); });

      // Arrastre con el mouse. En escritorio no hay gesto de swipe y la rueda
      // vertical no mueve un carrusel horizontal, así que sin esto la única
      // forma de navegar serían las flechas.
      var arrastrando = false, xInicial = 0, scrollInicial = 0, movido = 0;

      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;   // el táctil ya lo maneja el navegador
        arrastrando = true; movido = 0;
        xInicial = e.clientX;
        scrollInicial = track.scrollLeft;
        track.classList.add('is-dragging');
        track.style.scrollSnapType = 'none';
      });

      track.addEventListener('pointermove', function (e) {
        if (!arrastrando) return;
        var d = e.clientX - xInicial;
        movido = Math.abs(d);
        track.scrollLeft = scrollInicial - d;
      });

      function soltar() {
        if (!arrastrando) return;
        arrastrando = false;
        track.classList.remove('is-dragging');
        track.style.scrollSnapType = '';
        // Reencaja en la pieza más cercana; sin esto queda a media foto.
        var items = track.querySelectorAll('[data-mm-band-item]');
        if (items.length) {
          var w = items[0].getBoundingClientRect().width || 1;
          track.scrollTo({ left: Math.round(track.scrollLeft / w) * w, behavior: 'smooth' });
        }
      }
      track.addEventListener('pointerup', soltar);
      track.addEventListener('pointerleave', soltar);
      track.addEventListener('pointercancel', soltar);

      // Un arrastre no debe abrir el producto por accidente.
      track.addEventListener('click', function (e) {
        if (movido > 6) { e.preventDefault(); e.stopPropagation(); }
      }, true);

      // Flechas del teclado cuando la franja tiene el foco.
      track.setAttribute('tabindex', '0');
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); self.step(band, 1); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); self.step(band, -1); }
      });
    });

    // Al cambiar de tamaño de ventana el ancho de paso cambia.
    var t = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(t);
      t = window.setTimeout(function () { self.update(); }, 150);
    });
  };

  Mixer.prototype.indexOf = function (band) {
    var track = band.querySelector('[data-mm-band-track]');
    if (!track) return 0;
    var items = track.querySelectorAll('[data-mm-band-item]');
    if (!items.length) return 0;
    var w = items[0].getBoundingClientRect().width || 1;
    return Math.max(0, Math.min(items.length - 1, Math.round(track.scrollLeft / w)));
  };

  Mixer.prototype.step = function (band, dir) {
    var track = band.querySelector('[data-mm-band-track]');
    var items = track.querySelectorAll('[data-mm-band-item]');
    if (!items.length) return;
    var w = items[0].getBoundingClientRect().width || 1;
    var i = this.indexOf(band) + dir;

    // Da la vuelta en los extremos: en móvil no hay forma cómoda de volver.
    if (i < 0) i = items.length - 1;
    if (i > items.length - 1) i = 0;

    track.scrollTo({ left: i * w, behavior: 'smooth' });
  };

  Mixer.prototype.activePieces = function () {
    var self = this;
    return this.bands.map(function (band) {
      var items = band.querySelectorAll('[data-mm-band-item]');
      var el = items[self.indexOf(band)];
      if (!el || !el.dataset.mmProductId) return null;
      return {
        el: el,
        band: band.dataset.mmBandLabel || '',
        layer: band.dataset.mmBandLayer || '',
        id: el.dataset.mmProductId,
        title: el.dataset.mmProductTitle || '',
        vendor: el.dataset.mmProductVendor || '',
        url: el.dataset.mmProductUrl || '',
        price: parseInt(el.dataset.mmPrice, 10) || 0,
        variantId: el.dataset.mmVariant,
        available: el.dataset.mmAvailable === 'true',
        variants: parseVariants(el.dataset.mmVariants)
      };
    }).filter(Boolean);
  };

  /* ---------- bloque flotante ---------- */

  Mixer.prototype.update = function () {
    var pieces = this.activePieces();
    if (!pieces.length) return;

    var total = pieces.reduce(function (s, p) { return s + p.price; }, 0);
    var agotado = pieces.some(function (p) { return !p.available; });

    if (this.priceEl) this.priceEl.textContent = money(total);

    if (this.nameEl) {
      // El nombre del look es la pieza que manda: la de la primera franja.
      this.nameEl.textContent = this.namePrefix
        ? this.namePrefix + ' · ' + pieces[0].title
        : pieces[0].title;
    }

    if (this.addBtn) {
      this.addBtn.disabled = agotado;
      this.addBtn.textContent = agotado
        ? 'Una pieza está agotada'
        : (this.addBtn.dataset.mmLabel || this.addBtn.dataset.mmLabel === ''
            ? this.addBtn.dataset.mmLabel
            : this.addBtn.textContent);
      if (!this.addBtn.dataset.mmLabel && !agotado) {
        this.addBtn.dataset.mmLabel = this.addBtn.textContent;
      }
      if (!agotado && this.addBtn.dataset.mmLabel) {
        this.addBtn.textContent = this.addBtn.dataset.mmLabel;
      }
    }

    this.updateSeal(pieces);
    this.updateDots();
  };

  Mixer.prototype.updateDots = function () {
    var self = this;
    this.bands.forEach(function (band) {
      var dots = band.querySelector('[data-mm-band-dots]');
      if (!dots) return;
      var i = self.indexOf(band);
      var todos = dots.children;
      for (var k = 0; k < todos.length; k++) {
        todos[k].classList.toggle('is-active', k === i);
      }
    });
  };

  // El sello de curaduría se enciende cuando la combinación respeta las
  // reglas básicas de estilo. No bloquea nada: solo señala.
  Mixer.prototype.updateSeal = function (pieces) {
    if (!this.seal) return;
    var cfg = (window.mmSettings && window.mmSettings.wardrobe) || {};
    var ok = true;

    if (cfg.respectFormality !== false) {
      var f = pieces.map(function (p) { return dataset(p.el, 'mmFormalidad'); }).filter(Boolean);
      if (f.indexOf('formal') > -1 && f.indexOf('deportivo') > -1) ok = false;
    }

    if (cfg.flagPatterns !== false) {
      var pats = pieces.map(function (p) { return dataset(p.el, 'mmPatron'); })
                       .filter(function (x) { return x && x !== 'liso'; });
      if (pats.length > 1) ok = false;
    }

    this.seal.hidden = !ok;
  };

  /* ---------- agregar el look ---------- */

  Mixer.prototype.bindAdd = function () {
    var self = this;
    if (!this.addBtn) return;

    this.addBtn.addEventListener('click', function () {
      var pieces = self.activePieces();
      if (!pieces.length) return;

      var needSize = pieces.some(function (p) { return p.variants.length > 1; });

      if (needSize && self.sheet) {
        self.openSheet(pieces);
      } else {
        self.addToCart(pieces.map(function (p) {
          return { id: p.variantId, quantity: 1, piece: p };
        }));
      }
    });
  };

  Mixer.prototype.addToCart = function (lines) {
    var self = this;
    var lookId = 'mix-' + Date.now();

    var items = lines.map(function (l) {
      return {
        id: Number(l.id),
        quantity: l.quantity || 1,
        properties: {
          _look_id: lookId,
          _look_layer: (l.piece && l.piece.layer) || ''
        }
      };
    });

    if (this.addBtn) {
      this.addBtn.disabled = true;
      this.addBtn.dataset.mmBusy = '1';
      this.addBtn.textContent = 'Agregando…';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'No se pudo agregar'); });
        return r.json();
      })
      .then(function () {
        self.closeSheet();
        if (MONARA.CartDrawer && MONARA.CartDrawer.refresh) MONARA.CartDrawer.refresh();
        var tipo = (window.mmSettings && window.mmSettings.cartType) || 'drawer';
        if (tipo === 'drawer' && MONARA.CartDrawer && MONARA.CartDrawer.open) MONARA.CartDrawer.open();
        else if (tipo === 'page') window.location.href = '/cart';
        else if (MONARA.showToast) MONARA.showToast('Se agregó el look a tu bolsa');
      })
      .catch(function (err) {
        if (MONARA.showToast) MONARA.showToast(err.message);
        else window.alert(err.message);
      })
      .then(function () {
        if (self.addBtn) {
          self.addBtn.disabled = false;
          delete self.addBtn.dataset.mmBusy;
          self.addBtn.textContent = self.addBtn.dataset.mmLabel || 'Agregar este look';
        }
      });
  };

  /* ---------- hoja de tallas ---------- */

  Mixer.prototype.bindSheet = function () {
    if (!this.sheet) return;
    var self = this;

    this.sheet.querySelectorAll('[data-mm-sheet-close]').forEach(function (b) {
      b.addEventListener('click', function () { self.closeSheet(); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.sheet.getAttribute('aria-hidden') === 'false') self.closeSheet();
    });

    var submit = this.sheet.querySelector('[data-mm-sheet-submit]');
    if (submit) {
      submit.addEventListener('click', function () { self.submitSheet(); });
    }
  };

  Mixer.prototype.openSheet = function (pieces) {
    var body = this.sheet.querySelector('[data-mm-sheet-body]');
    var self = this;
    this.sheetPieces = pieces;

    body.innerHTML = pieces.map(function (p, idx) {
      var opciones = p.variants.map(function (v) {
        var libre = v.available;
        return '<button type="button" class="mm-sheet__size' + (libre ? '' : ' is-out') + '"'
             + ' data-mm-size data-mm-piece="' + idx + '" data-mm-variant="' + v.id + '"'
             + ' data-mm-price="' + v.price + '"' + (libre ? '' : ' disabled aria-disabled="true"') + '>'
             + esc(v.title) + '</button>';
      }).join('');

      return '<div class="mm-sheet__piece" data-mm-sheet-piece="' + idx + '">'
           + '<p class="mm-label mm-sheet__piece-band">' + esc(p.band) + '</p>'
           + '<div class="mm-sheet__piece-head">'
           +   '<span class="mm-sheet__piece-brand mm-label">' + esc(p.vendor) + '</span>'
           +   '<span class="mm-sheet__piece-title">' + esc(p.title) + '</span>'
           + '</div>'
           + '<div class="mm-sheet__sizes" role="group" aria-label="Talla de ' + esc(p.title) + '">' + opciones + '</div>'
           + '</div>';
    }).join('');

    body.querySelectorAll('[data-mm-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var grupo = btn.closest('.mm-sheet__sizes');
        grupo.querySelectorAll('[data-mm-size]').forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        self.refreshSheetTotal();
      });
    });

    // Preselecciona la primera talla disponible de cada pieza: ahorra un tap
    // y deja claro que el precio ya está completo.
    body.querySelectorAll('.mm-sheet__sizes').forEach(function (g) {
      var libre = g.querySelector('[data-mm-size]:not([disabled])');
      if (libre) libre.classList.add('is-active');
    });

    this.refreshSheetTotal();
    this.sheet.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('mm-no-scroll');
  };

  Mixer.prototype.refreshSheetTotal = function () {
    var body = this.sheet.querySelector('[data-mm-sheet-body]');
    var totalEl = this.sheet.querySelector('[data-mm-sheet-total]');
    var note = this.sheet.querySelector('[data-mm-sheet-note]');
    var submit = this.sheet.querySelector('[data-mm-sheet-submit]');

    var elegidas = body.querySelectorAll('[data-mm-size].is-active');
    var total = 0;
    elegidas.forEach(function (b) { total += parseInt(b.dataset.mmPrice, 10) || 0; });

    if (totalEl) totalEl.textContent = money(total);

    var faltan = this.sheetPieces.length - elegidas.length;
    if (note) {
      note.hidden = faltan === 0;
      note.textContent = faltan === 1
        ? 'Falta elegir la talla de una pieza'
        : 'Faltan tallas por elegir (' + faltan + ')';
    }
    if (submit) submit.disabled = faltan > 0;
  };

  Mixer.prototype.submitSheet = function () {
    var body = this.sheet.querySelector('[data-mm-sheet-body]');
    var self = this;
    var lines = [];

    body.querySelectorAll('[data-mm-size].is-active').forEach(function (b) {
      lines.push({
        id: b.dataset.mmVariant,
        quantity: 1,
        piece: self.sheetPieces[parseInt(b.dataset.mmPiece, 10)]
      });
    });

    if (lines.length !== this.sheetPieces.length) return;
    this.addToCart(lines);
  };

  Mixer.prototype.closeSheet = function () {
    if (!this.sheet) return;
    this.sheet.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('mm-no-scroll');
  };

  /* ---------- utilidades ---------- */

  function parseVariants(raw) {
    if (!raw) return [];
    try {
      var d = document.createElement('textarea');
      d.innerHTML = raw;
      var arr = JSON.parse(d.value);
      return arr.map(function (v) {
        return { id: v.id, title: v.title, price: v.price, available: v.available };
      });
    } catch (e) { return []; }
  }

  function dataset(el, key) {
    return el && el.dataset ? (el.dataset[key] || '') : '';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function money(cents) {
    if (MONARA.formatMoney) return MONARA.formatMoney(cents);
    return '$' + (cents / 100).toLocaleString('es-MX', { minimumFractionDigits: 2 });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { MONARA.Mixer.init(); });
  } else {
    MONARA.Mixer.init();
  }

  // El editor de temas recarga la sección sin recargar la página.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-mm-mixer]')) MONARA.Mixer.init();
  });
})();
